import { useSelector } from "react-redux";
import { useState, useEffect, ChangeEventHandler } from "react";
import { Dialog } from "@mui/material";
import { RootState } from "app/store";
import { isIos, useScale } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import I18n from "features/shared/i18n";

interface NicknameEditModalProps {
  open: boolean;
  currentName: string;
  onClose: () => void;
  onConfirm: (newName: string) => void;
}

export const NicknameEditModal = ({
  open,
  currentName,
  onClose,
  onConfirm,
}: NicknameEditModalProps) => {
  const { width, scale } = useScale();
  // 狭い画面（scale下限クランプ時）でも収まるよう、設計幅を上限に可変にする
  const modalWidth = Math.min(600, width / scale - 16);
  const [name, setName] = useState(currentName);
  const [error, setError] = useState("");
  const [disable, setDisable] = useState(false);
  const bannedWords = useSelector((state: RootState) => state.config.banned.words);

  useEffect(() => {
    if (open) {
      setName(currentName);
      setError("");
      setDisable(false);
    }
  }, [open, currentName]);

  const handleUserName: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target.value.length > 10) {
      setError(I18n.t("MSG_USER_NAME_LENGTH_LIMIT"));
    } else {
      if (error) {
        setError("");
      }
    }
    setName(e.target.value);
  };

  const handleClick = () => {
    if (!disable && !error) {
      setDisable(true);
      const trimmedName = name.trim();

      if (!trimmedName) {
        setError(I18n.t("MSG_USER_NAME_REQUIRED"));
        setDisable(false);
        return;
      }

      if (bannedWords.filter((word) => trimmedName.includes(word.word)).length > 0) {
        setError(I18n.t("MSG_PARENT_PAGE_NICKNAME_ERROR_MESSAGE"));
        setDisable(false);
        return;
      }

      onConfirm(trimmedName);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div style={{ width: modalWidth }} className="flex-col-el border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">edit</span>
                <h2 className="text-[33px] text-textcolor-black">
                  {I18n.t("MSG_PARENT_PAGE_NICKNAME_EDIT_TITLE")}
                </h2>
              </div>
              <div className="flex-col-view items-start py-4">
                <p className="text-body text-textcolor-black text-[24px]">
                  {I18n.t("MSG_PARENT_PAGE_NICKNAME_EDIT_MESSAGE")}
                </p>

                <div className="flex-col-el w-full relative my-4">
                  <input
                    type="text"
                    value={name}
                    autoFocus={!isIos()}
                    onChange={handleUserName}
                    className={`input-modal text-body text-textcolor-black text-[30px] ${
                      error && "!border-danger"
                    }`}
                  />

                  <label className="text-body text-left text-[21px] h-5 text-danger">
                    {error ? error : ""}
                  </label>
                </div>
              </div>

              <div className="flex-row-el flex-center gap-4">
                <button
                  onClick={onClose}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                >
                  {I18n.t("MSG_CANCEL_BTN_BY_PARENT")}
                </button>
                <button
                  onClick={handleClick}
                  disabled={name.trim().length === 0 || !!error}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {I18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
